<template>
  <div class="player-stats" :class="[`player-stats--${size}`, { 'player-stats--icons': icons, 'player-stats--inline': inline }]">
    <template v-if="inline">
      <span>{{ wins }} {{ pluralRu(wins, 'победа', 'победы', 'побед') }} · {{ losses }} {{ pluralRu(losses, 'поражение', 'поражения', 'поражений') }}</span>
    </template>
    <template v-else>
      <div class="player-stats__item">
        <AppIcon v-if="icons" name="star" :size="iconSize" class="player-stats__icon player-stats__icon--win" />
        <div class="player-stats__texts">
          <span class="player-stats__value cb-num">{{ wins }}</span>
          <span class="player-stats__label">{{ winsLabel }}</span>
        </div>
      </div>
      <span class="player-stats__divider" aria-hidden="true"></span>
      <div class="player-stats__item">
        <AppIcon
          v-if="icons"
          name="star-outline"
          :size="iconSize"
          :stroke-width="1.6"
          class="player-stats__icon player-stats__icon--loss"
        />
        <div class="player-stats__texts">
          <span class="player-stats__value cb-num">{{ losses }}</span>
          <span class="player-stats__label">{{ lossesLabel }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
/**
 * PlayerStats — «12 Побед | 3 Поражений» с вертикальным разделителем.
 *
 * <PlayerStats :wins="me.countWins" :losses="me.countLosses" />              // экраны 1, 8, 9, 10
 * <PlayerStats :wins="…" :losses="…" icons size="lg" />                      // экраны 4, 6 (звёзды)
 *
 * props: wins, losses, icons (звезда/контурная звезда слева), inline (короткая строка со склонениями), size ('md'|'lg'),
 *        winsLabel ('Побед'), lossesLabel ('Поражений').
 */
import { computed } from "vue";
import AppIcon from "./AppIcon.vue";
import { pluralRu } from "@/utils/format";

const props = defineProps({
  wins: { type: Number, default: 0 },
  losses: { type: Number, default: 0 },
  icons: { type: Boolean, default: false },
  inline: { type: Boolean, default: false },
  size: { type: String, default: "md", validator: (v) => ["md", "lg"].includes(v) },
  winsLabel: { type: String, default: "Побед" },
  lossesLabel: { type: String, default: "Поражений" },
});

const iconSize = computed(() => (props.size === "lg" ? 40 : 30));
</script>

<style scoped>
.player-stats {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;
  min-width: 0;
}

.player-stats__item {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.player-stats__texts {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.player-stats__value {
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
  color: var(--cb-text);
}

.player-stats__label {
  font-size: 14px;
  font-weight: 500;
  color: var(--cb-text-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.player-stats--lg .player-stats__value {
  font-size: 32px;
}

.player-stats--lg .player-stats__label {
  font-size: 15px;
}

.player-stats__divider {
  width: 1px;
  height: 44px;
  background: linear-gradient(180deg, rgba(221, 245, 90, 0) 0%, rgba(221, 245, 90, 0.4) 50%, rgba(221, 245, 90, 0) 100%);
}

.player-stats__icon--win {
  color: var(--cb-lime);
  filter: drop-shadow(0 0 6px rgba(221, 245, 90, 0.45));
}

.player-stats__icon--loss {
  color: var(--cb-text);
  opacity: 0.9;
}
.player-stats--inline { display: block; color: var(--cb-text-2); font-size: 12px; line-height: 1.35; font-variant-numeric: tabular-nums; }
.player-stats--inline > span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
</style>
